import { SvcGetWebshops, SvcListOrdersForCustomer } from "@springtree/eva-services-core";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";

import Button from "~/components/common/button";
import { useAuthContext } from "~/contexts/auth";
import { useBreakpoint } from "~/contexts/breakpoint";
import { useEvaService } from "~/hooks/use-eva-service";
import Constants from "~/utils/constants";

import styles from "./purchases.module.scss";
import PurchasesItem from "./purchases-item";

type Props = {
  returns?: boolean;
};

export default function Orders(props: Props) {
  const { user, isAuthenticated } = useAuthContext();
  const t = useTranslations();
  const breakpoint = useBreakpoint();
  const getWebshops = useEvaService(SvcGetWebshops);
  const listOrders = useEvaService(SvcListOrdersForCustomer);

  const query = useInfiniteQuery({
    queryKey: ["getOrdersList", user?.ID, !!props.returns],
    staleTime: Constants.DEFAULT_STALE_TIME,
    keepPreviousData: true,
    queryFn: async ({ pageParam = 0 }) => {
      const orderList = await listOrders({
        ID: user!.ID,
        PageConfig: {
          Start: pageParam,
          SortDirection: 1,
          // SortProperty: "PlacementDate",
          Limit: Constants.ORDERS_PAGE_SIZE,
          Filter: {
            ShowOrdersWithoutLines: true,
            OrderProperties: props.returns ? Constants.RETURN_ORDER : undefined,
            ExcludeOrderProperties: !props.returns ? Constants.RETURN_ORDER : undefined,
          },
        },
      });
      if (orderList) {
        return orderList.Result;
      }
      return null;
    },
    getNextPageParam: (lastPage) => {
      if (
        !!lastPage &&
        !!lastPage.PageConfig &&
        lastPage.NumberOfPages !== undefined &&
        lastPage.CurrentPage !== undefined &&
        lastPage.NumberOfPages > lastPage.CurrentPage
      ) {
        return lastPage.PageConfig.Start! + lastPage.Limit!;
      }

      return undefined;
    },
    enabled: !!isAuthenticated,
  });

  const { data: webshops } = useQuery({
    queryKey: ["getWebshops"],
    staleTime: Infinity,
    queryFn: async () => await getWebshops(),
  });

  const purchases = query.data?.pages
    .flatMap((page) => page?.Page ?? [])
    .filter((order) => {
      const filteredLines = order.Lines?.filter(
        (line) => !!line.ProductID && ((line.IsCancelled && line.FulfillmentStatus === 800) || !line.IsCancelled)
      );
      return (
        filteredLines &&
        filteredLines.length > 0 &&
        (props.returns ||
          (order.Payments && order.Payments.length > 0) ||
          filteredLines.some((line) => line.ShippingMethod?.PaymentHandledByCarrier))
      );
    });

  return (
    <>
      <h1 className={styles.purchasesTitleDesktop}>
        {t(props.returns ? "generic.myaccount.nav.returns" : "generic.myaccount.nav.purchases")}
        {/* ({query.data ? query.data.pages[0]?.Total : 0}) */}
      </h1>

      <div className={styles.purchasesList}>
        {purchases?.length == 0 && (
          <p className={styles.noOrdersYet}>{t(props.returns ? "generic.noReturnsYet" : "generic.noOrdersYet")}</p>
        )}
        {purchases?.map((order) => {
          return <PurchasesItem key={order.ID} order={order} returns={props.returns} />;
        })}
      </div>
      {query.hasNextPage && (
        <Button
          variant="secondary"
          className={styles.nextPage}
          disabled={query.isFetchingNextPage}
          loading={query.isFetchingNextPage}
          onClick={() => query.fetchNextPage()}
        >
          {t("generic.load_more")}
        </Button>
      )}
    </>
  );
}
